import { makeLink } from "simple-utils"
import { asString, firstDayOfMonth, lastDayOfMonth, next } from "simple-utils/dist/date-utils"
import { isPrivacyOn } from "../account/table/privacy-mode"
import type { FullReportEntry, ReportEntry } from "./FullReportEntry"

export interface Account {
    id: number
    metaId: string
    name: string
    state: string
    franchise: number
    group: string
    manager: string
    consultant: string
    status: number
    balance?: number
    pageId?: string
    lastStatusChangeDate?: Date 
    tags?: number[]
}

export enum BalanceKind {
    none = 0,
    normal = 1,
    warning = 2,
    danger = 3,
    card = 4 
}

const privacy = isPrivacyOn();

export function getFranchiseById(franchise:string | number):string{
    if(typeof franchise == 'string') 
        franchise = parseInt(franchise);

    if(privacy)
        return `Cliente ${franchise.toFixed().padStart(3, '0')}`

    switch(franchise){
        case 1: return "Royal Face";
        case 2: return "SNDN";
        case 3: return "Feel";
        case 5: return "Odonto Company";
        case 6: return "Novofio";
        case 7: return "Royal Face (Google)";
        case 8: return 'AWR';
        case 9: return 'Choice';
        case 10: return 'Mkt Doc';
        case 11: return 'Dr. Laser';
        case 12: return 'Digiclinic';
        case 14: return 'Novofio (Google)';
        case 15: return 'Botolifting';
        case 16: return 'Face Doctor';
        case 17: return 'Anjos Colchões';
        case 18: return 'Anjos Colchões (Google)';
        case 19: return 'SNDN (Google)';
        case 20: return 'Dayane';
        case 21: return 'NaBeauty';
        case 22: return 'Emagrecentro';
        default: return "Indefinida";
    }
}

export function getFranchise(account:Account):string{
    return getFranchiseById(account.franchise);
}

export function isBoleto(account:Account){
    return account.name.match(/boleto/i) != null;
}

export function balanceKind(account:FullReportEntry):BalanceKind {
    if(!isBoleto(account)) return BalanceKind.card;
    const min = account.dayBudget * 3;
    if(account.balance === null) return BalanceKind.none
    if(account.balance < min) return BalanceKind.danger;
    if(account.balance < min * 2) return BalanceKind.warning;
    return BalanceKind.normal
} 

export function leadGainRatio(account:FullReportEntry){
    if(!account.pastMonthSameDay.leads) return 1;
    if(!account.nLeads) return -1;
    const diff = account.nLeads - account.pastMonthSameDay.leads
    return diff / account.nLeads;
}

export function yesterdayLeadGainRatio(account:FullReportEntry){
    if(!account.pastMonthSameDay.leads) return 1;
    if(!account.yesterDayInfo.leads) return -1;
    const diff = account.yesterDayInfo.leads - account.pastMonthYesterDayInfo.leads
    return diff / account.yesterDayInfo.leads;

}

function toHours(s:number){
    return s / 1000 / 60 / 60;
}

export function getLeadsProjection(nLeads:number){
    if(nLeads == 0) return 0;


    var now = new Date();
    var first = firstDayOfMonth(now);
    var last = lastDayOfMonth(now)
    last.setHours(23)
    last.setMinutes(59);

    var pastHours =  toHours(now.getTime() - first.getTime())
    var lastHours =  toHours(last.getTime() - now.getTime())

    var avg = nLeads / pastHours;

    return (nLeads + avg * lastHours);
}

export function monthGoal(account:ReportEntry){
    return account?.goal ? (account.nLeads / account.goal) : 0
}

export function goalClass(account:ReportEntry){
    const mg = account.goalRatio || 0;
    if(mg >= 1) return 'bg-success';
    if(mg < .7) return 'bg-danger';
    return 'bg-secondary'
}

export function goalTextClass(account:ReportEntry){
    const mg = account.goalRatio || 0;
    if(mg >= 1) return 'text-success';
    if(mg < .7) return 'text-danger';
    return ''
}

export function goalStatus(account:ReportEntry){
    const mg = account.goalRatio || 0;
    if(mg == 0) return "Sem informação"
    if(mg >= 1) return 'Acima';
    if(mg < .7) return 'Muito abaixo';
    return 'Abaixo'
}

export function overrunStatus(account:ReportEntry){
    if(account.overrun > 1.15)
        return "Alto";
    if(account.overrun < .95)
        return "Baixo"
    return "Neutro";
}

export function goalStatusColor(status:string){
    if(status == "Sem informação") return "color20"
    if(status == "Acima") return 'color2';
    if(status == "Muito abaixo") return 'color4';
    return 'color3'
}

export function leadCost(account:ReportEntry):number {
    return account?.nLeads == 0 ? 0 : account.totalCost / account.nLeads;
}

export function suggestedBudget(account:ReportEntry):number {
    if(!account) return 0;
    return leadCost(account) * account.goal;
}

export function getAccountLink(account:Account){
    var today = asString(new Date());
    var tomorrowDt = asString(next(new Date()));
    return `https://business.facebook.com/adsmanager/manage/campaigns?act=${account.metaId}&date=${today}_${tomorrowDt}%2Ctoday&filter_set=ADGROUP_DELIVERY_STATUS-STRING_SET%1EIN%1E[%22active%22%2C%22pending%22]`
}

export function getBillingLink(account:Account){
    return `https://business.facebook.com/ads/manager/account_settings/account_billing/?act=${account.metaId}`;
}

export function getAdsLink(account:Account){
    return `https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=ALL&view_all_page_id=${account.pageId}&sort_data[direction]=desc&sort_data[mode]=relevancy_monthly_grouped&search_type=page&media_type=all`;
}

export function getPageLink(account:Account){
    return `https://www.facebook.com/${account.pageId}`; // /settings/?tab=whatsapp_management`;
}

export function getPageStatusLink(account:Account){
    return `https://www.facebook.com/business-support-home/${account.pageId}/?source=actor_page_ale_notif&do_on_load=open_request_review`;
}

function getSlug(account:Account){
    return makeLink((account.franchise == 1 ? '' : getFranchise(account) + " ") + account.name.replace(/ ?\(?Boleto\)?/i, ''))
}

export function getDetailReportLink(account:Account){
    return `https://${location.host}/relatorios/${getSlug(account)}`;
}

export function getAccountStatusLink(account:Account){
    return `https://www.facebook.com/business-support-home/${account.metaId}/?tab=available_for_review&source=spoke&asset_view_type=asset`;
}

export function isGoogle(account:Account){
    return account.metaId.startsWith("G-");
}


export function getGoogleLink(account:FullReportEntry){
    return `https://ads.google.com/aw/overview?ocid=${account.info?.googleCustomerId ?? account.pageId}`
}



export function getGooglePaymentLink(account:Account){
    return `https://ads.google.com/aw/billing/summary?ocid=${account.pageId}`
}


